"use client";

import { useMemo, useState } from "react";
import type { Locale } from "@/lib/i18n/locales";
import mandarin from "@/messages/ai-continuation-zh-Hans.json";
import {
  SiClaude,
  SiClaudeHex,
  SiOpenai,
  SiOpenaiHex,
} from "@icons-pack/react-simple-icons";
import { Check, Copy, ExternalLink, MessageCircle } from "lucide-react";

import {
  buildAiContinuationPrompt,
  type AiContinuationPromptInput,
} from "@/lib/ai-continuation-prompts";
import { event as gaEvent } from "@/lib/gtag";
import { trackJourneyEvent } from "@/lib/journey-analytics";
import { hasConsent } from "@/lib/legal/consent";

type AssistantId = "chatgpt" | "claude";

type Assistant = {
  id: AssistantId;
  label: string;
  Icon: typeof SiOpenai;
  color: string;
  url: (prompt: string, locale?: Locale) => string;
  // Describes the URL we send, not whether the visitor submits the prompt.
  prefillsPrompt: boolean;
};

const ECLAT_CHATGPT_PLUGIN_URL =
  "https://chatgpt.com/plugins/plugin_asdk_app_6a4782b79a488191a130116ec8a8abb3";
const ECLAT_CHATGPT_PLUGIN_INSTRUCTION = [
  "Use the Eclat Institute plugin for this request.",
  "If the plugin is unavailable, stop before answering and ask me to install it first:",
  ECLAT_CHATGPT_PLUGIN_URL,
  "After installation, ask me to start a new chat and open this page's ChatGPT button again.",
].join("\n");

const ASSISTANTS: readonly Assistant[] = [
  {
    id: "chatgpt",
    label: "ChatGPT",
    Icon: SiOpenai,
    color: SiOpenaiHex,
    url: (prompt, locale = "en") => {
      const instruction =
        locale === "zh-Hans"
          ? [
              mandarin.plugin.require,
              mandarin.plugin.unavailable,
              ECLAT_CHATGPT_PLUGIN_URL,
              mandarin.plugin.after,
            ].join("\n")
          : ECLAT_CHATGPT_PLUGIN_INSTRUCTION;
      return `https://chatgpt.com/?q=${encodeURIComponent(
        `${instruction}\n\n${prompt}`
      )}`;
    },
    prefillsPrompt: true,
  },
  {
    id: "claude",
    label: "Claude",
    Icon: SiClaude,
    color: SiClaudeHex,
    url: (prompt) => `https://claude.ai/new?q=${encodeURIComponent(prompt)}`,
    prefillsPrompt: true,
  },
];

// GA4 event names. `ai_continuation_click` is the existing intent metric (do
// not rename — it preserves continuity). The copy-result event is a new
// clipboard-reliability diagnostic.
const CLICK_EVENT = "ai_continuation_click";
const COPY_RESULT_EVENT = "ai_continuation_copy_result";

function assistantOrigin(assistant: Assistant, prompt: string): string {
  try {
    return new URL(assistant.url(prompt)).origin;
  } catch {
    return "";
  }
}

type AiContinuationButtonsProps = AiContinuationPromptInput & {
  className?: string;
  variant?: "default" | "compact" | "hero" | "inline" | "text" | "utility";
};

async function copyPrompt(prompt: string) {
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(prompt);
      return;
    } catch {
      // Fall back to the legacy copy path below.
    }
  }

  const textarea = document.createElement("textarea");
  textarea.value = prompt;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!copied) {
    throw new Error("Clipboard copy failed.");
  }
}

export default function AiContinuationButtons({
  className = "",
  variant = "default",
  locale = "en",
  pageType,
  title,
  url,
  subject,
  description,
}: AiContinuationButtonsProps) {
  const isMandarin = locale === "zh-Hans";
  const copy = mandarin.ui;
  const copyError = isMandarin
    ? copy.copyError
    : "Copy failed. Open an assistant, paste this page link, and ask your question.";
  const prompt = useMemo(
    () =>
      buildAiContinuationPrompt({
        locale,
        pageType,
        title,
        url,
        subject,
        description,
      }),
    [description, locale, pageType, subject, title, url]
  );
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");

  // target_id is shared by click and copy diagnostics. Keep the existing
  // event name for history; a click measures intent, not external submission.
  const trackClick = (
    action: string,
    options: { promptPrefilled?: boolean; targetUrl?: string } = {}
  ) => {
    trackJourneyEvent(CLICK_EVENT, {
      label: pageType,
      ...(options.promptPrefilled !== undefined
        ? { prompt_prefilled: options.promptPrefilled ? "prefilled" : "blank" }
        : {}),
      journey_name: "ai_continuation",
      cta_family: "ai_assistant",
      source_surface: "ai_continuation_buttons",
      target_type: "ai_assistant",
      target_id: action,
      commercial_intent: "informational",
      is_primary_cta: false,
      ...(options.targetUrl ? { target_url: options.targetUrl } : {}),
    });
  };

  // Clipboard success/failure is a GA4-only diagnostic — keep it out of the
  // first-party CTA funnel log to avoid diluting genuine intent rows.
  const trackCopyResult = (action: string, success: boolean) => {
    if (!hasConsent("analytics")) return;
    gaEvent(COPY_RESULT_EVENT, {
      target_id: action,
      label: pageType,
      copy_result: success ? "success" : "fail",
    });
  };

  const handleOpenAssistant = (assistant: Assistant) => {
    trackClick(assistant.id, {
      promptPrefilled: assistant.prefillsPrompt,
      targetUrl: assistantOrigin(assistant, prompt),
    });
    // Open synchronously with the prefilled URL. Only Copy prompt writes
    // to the clipboard. External loading and submission are not observable.
    window.open(assistant.url(prompt, locale), "_blank", "noopener,noreferrer");
  };

  const handleCopy = async () => {
    trackClick("copy_prompt");
    try {
      await copyPrompt(prompt);
      setStatus("copied");
      trackCopyResult("copy_prompt", true);
    } catch {
      setStatus("error");
      trackCopyResult("copy_prompt", false);
    }
  };

  if (variant === "hero") {
    return (
      <section
        className={`not-prose text-left ${className}`}
        aria-label={
          isMandarin ? copy.programmeLabel : "Ask AI about this programme"
        }
      >
        <p className="text-sm font-medium text-blue-100">
          <span>
            {isMandarin ? copy.quickAnswer : "Prefer a quick answer?"}
          </span>
          <span className="ml-1 font-bold text-white">
            {isMandarin ? copy.askAi : "Ask AI:"}
          </span>
        </p>
        <div
          className="mt-2 flex flex-wrap items-center gap-2"
          aria-label={isMandarin ? copy.options : "AI assistant options"}
        >
          {ASSISTANTS.map((assistant) => (
            <button
              key={assistant.id}
              type="button"
              onClick={() => handleOpenAssistant(assistant)}
              className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white backdrop-blur-sm transition hover:border-white/40 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-950"
              aria-label={`${isMandarin ? copy.open : "Open"} ${
                assistant.label
              }`}
            >
              <assistant.Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {assistant.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-sm font-semibold text-white backdrop-blur-sm transition hover:border-white/40 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-blue-950"
          >
            {status === "copied" ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {status === "copied"
              ? isMandarin
                ? copy.copied
                : "Prompt copied"
              : isMandarin
              ? copy.copy
              : "Copy prompt"}
          </button>
        </div>
        {status === "error" ? (
          <p className="mt-2 text-xs leading-5 text-blue-100">{copyError}</p>
        ) : null}
      </section>
    );
  }

  if (variant === "inline") {
    return (
      <section
        className={`not-prose text-left ${className}`}
        aria-label={isMandarin ? copy.options : "AI assistant options"}
      >
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-end">
          <span className="col-span-2 text-sm font-medium text-slate-500 sm:mr-1">
            {isMandarin ? copy.continue : "Continue with AI"}
          </span>
          {ASSISTANTS.map((assistant) => (
            <button
              key={assistant.id}
              type="button"
              onClick={() => handleOpenAssistant(assistant)}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100 sm:justify-start"
            >
              <assistant.Icon
                className="h-4 w-4"
                color={assistant.color}
                aria-hidden="true"
              />
              {assistant.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100 sm:justify-start"
          >
            {status === "copied" ? (
              <Check className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {status === "copied"
              ? isMandarin
                ? copy.copied
                : "Prompt copied"
              : isMandarin
              ? copy.copyMine
              : "Copy my prompt"}
          </button>
        </div>
        {status === "error" ? (
          <p className="mt-2 text-right text-xs leading-5 text-slate-500">
            {copyError}
          </p>
        ) : null}
      </section>
    );
  }

  if (variant === "compact") {
    return (
      <section
        className={`not-prose text-left ${className}`}
        aria-label={isMandarin ? copy.options : "AI assistant options"}
      >
        <div className="flex flex-wrap items-center justify-end gap-2">
          {ASSISTANTS.map((assistant) => (
            <button
              key={assistant.id}
              type="button"
              onClick={() => handleOpenAssistant(assistant)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white/85 text-slate-800 shadow-sm transition hover:border-eclat-300 hover:bg-eclat-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-eclat-200"
              aria-label={`${isMandarin ? copy.open : "Open"} ${
                assistant.label
              }`}
            >
              <assistant.Icon
                className="h-4 w-4"
                color={assistant.color}
                aria-hidden="true"
              />
            </button>
          ))}
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="inline-flex min-h-9 items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-eclat-200 sm:text-sm"
          >
            {status === "copied" ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {status === "copied"
              ? isMandarin
                ? copy.copied
                : "Prompt copied"
              : isMandarin
              ? copy.copy
              : "Copy prompt"}
          </button>
        </div>
        {status === "error" ? (
          <p className="mt-2 text-right text-xs leading-5 text-slate-500">
            {copyError}
          </p>
        ) : null}
      </section>
    );
  }

  if (variant === "utility") {
    return (
      <section
        className={`not-prose text-left ${className}`}
        aria-label={isMandarin ? copy.options : "AI assistant options"}
      >
        <div className="flex flex-wrap items-start gap-3">
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100"
          >
            {status === "copied" ? (
              <Check className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Copy className="h-4 w-4" aria-hidden="true" />
            )}
            {status === "copied"
              ? isMandarin
                ? copy.copied
                : "Prompt copied"
              : isMandarin
              ? copy.copy
              : "Copy prompt"}
          </button>
          <details className="group relative">
            <summary className="flex min-h-10 cursor-pointer list-none items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100 [&::-webkit-details-marker]:hidden">
              {isMandarin ? copy.openInAi : "Open in AI"}
            </summary>
            <div className="mt-2 flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2">
              {ASSISTANTS.map((assistant) => (
                <button
                  key={assistant.id}
                  type="button"
                  onClick={() => handleOpenAssistant(assistant)}
                  className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:text-blue-900 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100"
                  aria-label={`${isMandarin ? copy.open : "Open"} ${
                    assistant.label
                  }`}
                >
                  <assistant.Icon
                    className="h-4 w-4"
                    color={assistant.color}
                    aria-hidden="true"
                  />
                  {assistant.label}
                </button>
              ))}
            </div>
          </details>
        </div>
        {status === "error" ? (
          <p className="mt-2 text-xs leading-5 text-slate-500">{copyError}</p>
        ) : null}
      </section>
    );
  }

  if (variant === "text") {
    return (
      <section
        className={`not-prose text-left ${className}`}
        aria-label={isMandarin ? copy.options : "AI assistant options"}
      >
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
          {ASSISTANTS.map((assistant) => (
            <button
              key={assistant.id}
              type="button"
              onClick={() => handleOpenAssistant(assistant)}
              className="inline-flex min-h-10 items-center gap-2 border-b border-transparent px-0.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              aria-label={`${isMandarin ? copy.open : "Open"} ${
                assistant.label
              }`}
            >
              <assistant.Icon
                className="h-3.5 w-3.5"
                color={assistant.color}
                aria-hidden="true"
              />
              {assistant.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void handleCopy()}
            className="inline-flex min-h-10 items-center gap-2 border-b border-transparent px-0.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          >
            {status === "copied" ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {status === "copied"
              ? isMandarin
                ? copy.copied
                : "Prompt copied"
              : isMandarin
              ? copy.copy
              : "Copy prompt"}
          </button>
        </div>
        {status === "error" ? (
          <p className="mt-2 text-xs leading-5 text-slate-500">{copyError}</p>
        ) : null}
      </section>
    );
  }

  return (
    <section
      className={`not-prose rounded-xl border border-slate-200 bg-white/85 p-4 text-left shadow-sm sm:p-5 ${className}`}
      aria-labelledby="ai-continuation-title"
    >
      <div>
        <h2
          id="ai-continuation-title"
          className="text-base font-semibold leading-tight text-slate-950 sm:text-lg"
        >
          {isMandarin ? copy.heading : "Ask about this page"}
        </h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">
          {isMandarin
            ? copy.intro
            : "Open this page in an AI assistant, or copy a prompt and add your own question."}
        </p>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {ASSISTANTS.map((assistant) => (
          <button
            key={assistant.id}
            type="button"
            onClick={() => handleOpenAssistant(assistant)}
            className="inline-flex min-h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-eclat-300 hover:bg-eclat-50 hover:text-eclat-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-eclat-200"
          >
            <MessageCircle className="h-4 w-4" aria-hidden="true" />
            {assistant.label}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        ))}
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="inline-flex min-h-10 items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-eclat-200"
        >
          {status === "copied" ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          {status === "copied"
            ? isMandarin
              ? copy.copied
              : "Prompt copied"
            : isMandarin
            ? copy.copy
            : "Copy prompt"}
        </button>
      </div>

      <p className="mt-3 text-xs leading-5 text-slate-500">
        {status === "error"
          ? copyError
          : isMandarin
          ? copy.ready
          : "Open an assistant with a ready-to-send prompt, or copy it to use elsewhere."}
      </p>
    </section>
  );
}
